// src/components/dashboards/charts/useChartTheme.ts
// Provides theme-aware ECharts defaults with responsive support.

import { useTheme, alpha, useMediaQuery } from "@mui/material";
import { useMemo } from "react";
import { glass } from "../../../theme/glassTokens";
import { athenaColors } from "../../../theme/palette";
import { useIsDashboardSurface } from "../dashboardSurface";

/**
 * A series colour as a gradient rather than a flat fill. ECharts takes this
 * object anywhere a colour string goes.
 *
 * `orientation` is the direction the bar GROWS, so the solid end sits at the
 * bar's origin and the fade at its tip: a horizontal bar fades left to right,
 * a vertical one fades top to bottom.
 */
/** Linear mix between two hex colours, `t` in [0, 1]. */
export const mixToward = (from: string, to: string, t: number): string => {
  const parse = (c: string) =>
    [1, 3, 5].map((i) => parseInt(c.slice(i, i + 2), 16));
  const [r1, g1, b1] = parse(from);
  const [r2, g2, b2] = parse(to);
  const ch = (a: number, b: number) =>
    Math.round(a + (b - a) * t)
      .toString(16)
      .padStart(2, "0");
  return `#${ch(r1, r2)}${ch(g1, g2)}${ch(b1, b2)}`;
};

export const seriesGradient = (
  color: string,
  orientation: "horizontal" | "vertical" = "vertical",
  /** Fade toward this colour instead of toward transparency. */
  toward?: string,
  /** How far to travel toward it. 0.85 buries the source hue; on a pie, where
   *  the hue IS the label, 0.55-0.6 keeps it recognisable at both ends. */
  reach = 0.85
) => ({
  type: "linear" as const,
  x: 0,
  y: 0,
  x2: orientation === "horizontal" ? 1 : 0,
  y2: orientation === "horizontal" ? 0 : 1,
  colorStops: [
    { offset: 0, color },
    {
      offset: 1,
      color: toward ? mixToward(color, toward, reach) : alpha(color, 0.45),
    },
  ],
});

const useChartTheme = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // The dashboard renders these charts on the dark glass. The tokens
  // below otherwise resolve through contentText to deepNavy and blueGray, which
  // are correct on the light dashboard and invisible on the dark one: axis
  // labels, category names and the series colour all disappear.
  // NOT a constant: the superadmin panel's charts use this hook too and must
  // keep their light ink. See dashboardSurface.ts.
  const dark = useIsDashboardSurface();

  return useMemo(() => {
    const textPrimary = dark
      ? glass.textPrimary
      : theme.custom?.contentText?.primary || theme.palette.text.primary;
    const textSecondary = dark
      ? glass.textSecondary
      : theme.custom?.contentText?.secondary || theme.palette.text.secondary;

    // Branded palette for chart series
    // primary.main is deepNavy: a navy bar on a navy card. On the dark surface
    // the series must come from the light family instead.
    // periwinkle ALONE reads as grey on navy: it is a 12% saturation tint, and
    // the eye has nothing to call blue. Leading with it made every chart look
    // colourless. The primary series now runs periwinkle -> royalBlue as a
    // gradient (see seriesGradient's `toward`), which is the brand's actual
    // blue with the pale tint only as its highlight.
    // Athena's own family, in one ramp: periwinkle -> royalBlue -> purple ->
    // orange. The previous dark list borrowed MUI's success.light and
    // error.light, so a sunburst came out green-and-red and a treemap
    // green-and-pink: readable, but not this product's colours and not
    // compatible with each other. Intermediate steps are mixed from the brand
    // hexes rather than invented, so nothing here is off-palette.
    const blueMid = mixToward(athenaColors.royalBlue, athenaColors.periwinkle, 0.5);
    const blueDeep = mixToward(athenaColors.royalBlue, athenaColors.periwinkle, 0.22);
    const mauve = mixToward(athenaColors.softPurple, athenaColors.periwinkle, 0.45);
    const seriesColors = dark
      ? [
          athenaColors.periwinkle,
          blueMid,
          athenaColors.lightPurple,
          athenaColors.softPurple,
          athenaColors.lightOrange,
          blueDeep,
          athenaColors.orange,
          mauve,
          mixToward(athenaColors.lightPurple, athenaColors.periwinkle, 0.5),
          mixToward(athenaColors.orange, athenaColors.periwinkle, 0.55),
        ]
      : [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.error.main,
      "#ac61b9", // softPurple
      "#FFB347", // lightOrange
      "#36B37E", // success light
      "#0b468c", // royalBlue
    ];

    const fontSize = isMobile ? 9 : 11;

    const baseAxis = {
      axisLine: {
        lineStyle: { color: dark ? glass.headerDivider : theme.palette.divider },
      },
      axisLabel: { color: textSecondary, fontSize },
      axisTick: { show: false },
      splitLine: {
        lineStyle: {
          color: dark ? glass.rowDivider : theme.palette.divider,
          type: "dashed" as const,
        },
      },
    };

    const baseTooltip = {
      backgroundColor: dark ? glass.tableHeader : theme.palette.background.paper,
      borderColor: dark ? glass.panelBorder : theme.palette.divider,
      textStyle: { color: textPrimary, fontSize: isMobile ? 10 : 12 },
      extraCssText: `backdrop-filter:blur(12px);border-radius:8px;box-shadow:0 4px 24px ${alpha(theme.palette.common.black, 0.2)};`,
      confine: true,
    };

    const baseGrid = {
      left: "3%",
      right: "4%",
      bottom: "3%",
      top: "12%",
      containLabel: true,
    };

    /** Default chart height: smaller on mobile */
    const chartHeight = isMobile ? 240 : 300;

    return {
      dark,
      textPrimary,
      textSecondary,
      seriesColors,
      baseAxis,
      baseTooltip,
      baseGrid,
      theme,
      isMobile,
      chartHeight,
    };
  }, [theme, isMobile, dark]);
};

export default useChartTheme;
